import { useEffect, useState } from 'react';
import { useSnakeMarketsFindSeries } from '@/lib/endpoints';
import type { SnakeCurrencyDto, SnakeMarketsFindSeriesSubCategoryCode } from '@/lib/models';
import { centsToPercent } from '@/lib/binary-market';
import { cn } from '@/lib/utils';
import { upPercent, parsePrice, defaultLobbyPeriod } from '@/lib/snake-market';
import { useNowMs } from './useNowMs';
import { useSnakeAutoAdvance } from './useSnakeAutoAdvance';
import { useSnakePriceToBeatReady } from './useSnakePriceToBeatReady';
import { SnakePriceSubscriber } from './SnakePriceSubscriber';
import { LightweightChart } from '../LiveTrading/LightweightChart';

/** Live-window state the card reports up to the feed header (countdown notch). */
export interface SnakeLobbyCardMeta {
  closeTime: string | null;
  isAdvancing: boolean;
}

export interface SnakeLobbyCardProps {
  /** The currency this card previews (the active tab in the feed). */
  active: SnakeCurrencyDto;
  /** Tap-anywhere handler — forwards to the snake page (§5). */
  onOpen: () => void;
  /** Reports the live window's close time / boundary state up to the feed header. */
  onMeta?: (meta: SnakeLobbyCardMeta) => void;
}

/**
 * Body of the home "Short Markets" snake widget (ARC-84 §4, redesigned per
 * Figma 5751-141388) — a **display-only live preview** of the active
 * currency's default (5-minute) market.
 *
 * Shows the asset icon + `{asset} Up or Down` with a live spot-vs-target
 * indicator (`$X.XX Above/Below the Target`), the display-only dollar-price
 * graph (with a Target line), and **subtle Up/Down chips showing each side's
 * live chance**. **Tapping anywhere forwards to the snake page** — there is no
 * buy and no set-amount here. Currency switching is handled by the tab row in
 * {@link SnakeLobbyFeed} (in place, no navigation), which also renders the
 * countdown notch from this card's reported close time. Keyed on the
 * instrument by the feed so switching currency remounts → fresh series +
 * realtime.
 */
export function SnakeLobbyCard({ active, onOpen, onMeta }: SnakeLobbyCardProps) {
  const nowMs = useNowMs();
  const [upCents, setUpCents] = useState<number | null>(null);
  const [spot, setSpot] = useState<number | null>(null);

  const period = defaultLobbyPeriod(active.resolutionPeriods);
  const subCategoryCode = period?.subCategoryCode as SnakeMarketsFindSeriesSubCategoryCode | undefined;

  const { data: series, refetch } = useSnakeMarketsFindSeries(
    { instrument: active.instrument, subCategoryCode: subCategoryCode! },
    { query: { enabled: Boolean(subCategoryCode) } },
  );

  // Auto-advance the preview to the next live instance when this one closes —
  // otherwise the card sticks on a dead window (same fix as the snake page).
  const { isAdvancing } = useSnakeAutoAdvance(
    { marketId: series?.marketId, externalMarketId: series?.externalMarketId, closeTime: series?.closeTime },
    nowMs,
    refetch,
  );

  const closeTime = series?.closeTime ?? null;
  useEffect(() => {
    onMeta?.({ closeTime, isAdvancing });
  }, [onMeta, closeTime, isAdvancing]);

  // At a boundary the price-to-beat isn't known until the prior settles; show
  // pending (—) rather than a provisional Up%, and refetch until it resolves.
  const priceToBeatReady = series?.priceToBeatReady ?? true;
  useSnakePriceToBeatReady(Boolean(series) && !priceToBeatReady, refetch);

  const up = !priceToBeatReady
    ? null
    : upCents != null
      ? centsToPercent(upCents)
      : upPercent(series?.outcome?.yesPrice ?? null, undefined);
  const upPct = up ?? 50;
  const downPct = 100 - upPct;
  const strike = parsePrice(series?.outcome?.strikePrice ?? null);
  const previous = parsePrice(series?.resolution?.previousResolutionPrice ?? null);
  const basePrice = strike ?? previous ?? 65_080;
  const title = `${active.shortName ?? active.name ?? active.instrument} Up or Down`;

  // Live spot vs the price to beat — "$X.XX Above/Below the Target".
  const delta = spot != null && strike != null && priceToBeatReady ? spot - strike : null;
  const aboveTarget = delta != null && delta >= 0;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      aria-label={`Open ${title}`}
      className="flex w-full cursor-pointer flex-col gap-4 rounded-b-[12px] border-[1.5px] border-t-0 border-stroke-soft bg-surface px-5 py-4 text-left"
    >
      {series && subCategoryCode && (
        <SnakePriceSubscriber
          marketId={series.marketId}
          externalMarketId={series.externalMarketId}
          instrument={active.instrument}
          onPrices={(u) => setUpCents(u)}
          onSpot={(s) => setSpot(s)}
          onTradeDot={() => {}}
        />
      )}

      {/* Title + live spot-vs-target indicator */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {active.imageUrl ? (
            <img src={active.imageUrl} alt="" className="size-[30px] shrink-0 rounded-full object-cover" />
          ) : (
            <div className="size-[30px] shrink-0 rounded-full bg-stroke-soft" />
          )}
          <h3 className="m-0 truncate text-[18px] font-medium capitalize leading-tight text-ink">{title}</h3>
        </div>
        {delta != null && (
          <div className="flex shrink-0 items-center gap-1 text-[12px]">
            <svg viewBox="0 0 12 12" className={cn('size-3', aboveTarget ? 'fill-up' : 'rotate-180 fill-down')} aria-hidden>
              <path d="M6 1 L11 9 H1 Z" />
            </svg>
            <span className={cn('font-semibold', aboveTarget ? 'text-up' : 'text-down')}>${Math.abs(delta).toFixed(2)}</span>
            <span className="text-ink">{aboveTarget ? 'Above the Target' : 'Below the Target'}</span>
          </div>
        )}
      </div>

      {/* Graph — display-only (pan/zoom off; taps fall through to navigate) */}
      <div className="relative h-[200px] w-full">
        <LightweightChart instrumentSymbol={active.instrument} basePrice={basePrice} targetPrice={strike} />
      </div>

      {/* Up / Down — display-only subtle chips, each shows its side's live chance. */}
      <div className="flex gap-2 px-1">
        <span className="flex h-10 flex-1 items-center justify-center gap-1 rounded-pill bg-chip-green text-up">
          <span className="text-[16px] font-semibold tracking-[-0.32px]">Up</span>
          <span className="text-[16px] font-semibold tracking-[-0.32px]">{up == null ? '—' : `${upPct}%`}</span>
        </span>
        <span className="flex h-10 flex-1 items-center justify-center gap-1 rounded-pill bg-chip-red text-down">
          <span className="text-[16px] font-semibold tracking-[-0.32px]">Down</span>
          <span className="text-[16px] font-semibold tracking-[-0.32px]">{up == null ? '—' : `${downPct}%`}</span>
        </span>
      </div>
    </div>
  );
}
